import React, { useEffect, useMemo, useState } from "react";
import Switch from "@material-ui/core/Switch";
import Button from "../../shared/Button";
import swal from "sweetalert";
import { useARRCode } from "../../../context/ARRLevelContext";
import CustomModal from "../../shared/CustomModal";
import {
  useAssessmentScale,
  useAssociateAssessmentScale,
  useSelectAssessmentScaleLevel,
} from "../../../hooks/useAssessmentScale";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Spinner } from "reactstrap";
import Select from "../../shared/Select";
import { URL } from "../../../../../utils/Constants";

const showScoreAsPercentageOption = [
  {
    label: "Show both score and percentage",
    value: "both",
  },
  {
    label: "Show Percentage",
    value: "pctg",
  },
  {
    label: "Don't show percentage",
    value: "Nopctg",
  },
];

const showAggregationAsPercentageOption = [
  {
    label: "Show both aggregation and percentage",
    value: "aggregateAndPer",
  },
  {
    label: "Show only percentage",
    value: "per",
  },
  {
    label: "Don't show percentage",
    value: "noPer",
  },
];

const MarksAssessmentForm = ({
  data,
  type,
  scaleType,
  updating,
  handleUpdateAssessment,
  closeModal,
}) => {
  const { loading, createAssessmentScale } = useAssessmentScale();
  const { arrLevel } = useARRCode();
  const { associateAssessmentLevel, getAssociateAssessmentLevel } =
    useAssociateAssessmentScale();
  const { assessmentScaleLevel, selectAssessmentScaleLevel } =
    useSelectAssessmentScaleLevel();
  const [associateLevelModal, setAssociateLevelModal] = useState(false);
  const [marksAssessmentScale, setMarksAssessmentScale] = useState({
    scaleName: "",
    scoreName: "",
    rangeMin: 0,
    rangeMax: 100,
    theoryRangeMin: 0,
    theoryRangeMax: 100,
    practicalRangeMin: 0,
    practicalRangeMax: 0,
    minimumAchievement: 35,
    showScoreAsPercentage: "",
    calculateAggregate: false,
    showAggregationAspercentage: "",
  });
  const [associateScalePercent, setAssociateScalePercent] = useState([]);
  const [associateSelectInput, setAssociateSeletInput] = useState(null);

  const isView = type === "view";

  const associateAssessmentLevelOptions = useMemo(() => {
    if (!associateAssessmentLevel) return;
    return associateAssessmentLevel?.map((item) => ({
      label: item.scaleName,
      value: item.id,
    }));
  }, [associateAssessmentLevel]);

  const handleMarksAssessmentChange = (e) => {
    const { name, value } = e.target;

    if (name === "rangeMax") {
      setMarksAssessmentScale((prev) => ({
        ...prev,
        theoryRangeMax: value,
        practicalRangeMax: 0,
        [name]: value,
      }));

      return;
    }

    if (name === "theoryRangeMax" || name === "practicalRangeMax") {
      if (Number(value) > marksAssessmentScale.rangeMax) {
        swal("Oops", "Max value cannot exceed range max value");
        return;
      }
    }

    if (name === "minimumAchievement") {
      if (Number(value) > marksAssessmentScale.rangeMax) {
        swal("Oops", "Min Achievement cannot exceed range max value");
        return;
      }
    }

    if (name === "theoryRangeMax") {
      const newPracticalMax =
        Number(marksAssessmentScale.rangeMax) - Number(value);
      setMarksAssessmentScale((prev) => ({
        ...prev,
        practicalRangeMax: newPracticalMax,
        [name]: value,
      }));

      return;
    }

    if (name === "practicalRangeMax") {
      const newTheoryMax =
        Number(marksAssessmentScale.rangeMax) - Number(value);
      setMarksAssessmentScale((prev) => ({
        ...prev,
        theoryRangeMax: newTheoryMax,
        [name]: value,
      }));

      return;
    }

    setMarksAssessmentScale((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateNegativeNumber = (e) => {
    if (e.key === "-" || e.key === "Subtract" || e.key === "e") {
      e.preventDefault();
    }
  };

  const handleSubmit = async () => {
    if (!marksAssessmentScale?.scaleName.trim()) {
      swal("Oops", "Scale Name is required");
      return;
    }

    if (!marksAssessmentScale?.scoreName.trim()) {
      swal("Oops", "Score Name is required");
      return;
    }

    if (
      marksAssessmentScale.theoryRangeMin === "" ||
      marksAssessmentScale.theoryRangeMax === ""
    ) {
      swal("Oops", "Theory Range is required");
      return;
    }

    if (
      marksAssessmentScale.practicalRangeMin === "" ||
      marksAssessmentScale.practicalRangeMax === ""
    ) {
      swal("Oops", "Practical Range is required");
      return;
    }

    if (
      marksAssessmentScale.rangeMin === "" ||
      marksAssessmentScale.rangeMax === ""
    ) {
      swal("Oops", "Range is required");
      return;
    }

    if (marksAssessmentScale.minimumAchievement === "") {
      swal("Oops", "Minimum Achievement is required");
      return;
    }

    if (!marksAssessmentScale.showScoreAsPercentage) {
      swal("Oops", "Show Score As Percentage is required");
      return;
    }

    if (!marksAssessmentScale.showAggregationAspercentage) {
      swal("Oops", "Please select Show Aggregation As Percentage");
      return;
    }

    const assessmentScaleMarkLevels =
      associateScalePercent?.length > 0
        ? associateScalePercent?.map((item) => ({
            rangeMin: item.rangeMin,
            rangeMax: item.rangeMax,
            levelId: item.id,
          }))
        : [];

    const formData = {
      assessmentScaleTypeCode: scaleType,
      scaleName: marksAssessmentScale.scaleName,
      scoreName: marksAssessmentScale.scoreName,
      assessmentScaleMark: {
        rangeMin: marksAssessmentScale.rangeMin,
        rangeMax: marksAssessmentScale.rangeMax,
        minAchievementLevel: marksAssessmentScale.minimumAchievement,
        showPercentage:
          marksAssessmentScale.showScoreAsPercentage === "pctg"
            ? true
            : marksAssessmentScale.showScoreAsPercentage === "both"
              ? true
              : false,
        showScore:
          marksAssessmentScale.showScoreAsPercentage === "Nopctg"
            ? true
            : marksAssessmentScale.showScoreAsPercentage === "both"
              ? true
              : false,
        showAggregatedPercentage:
          marksAssessmentScale.showAggregationAspercentage === "per"
            ? true
            : marksAssessmentScale.showAggregationAspercentage ===
                "aggregateAndPer"
              ? true
              : false,
        showAggregatedScore:
          marksAssessmentScale.showAggregationAspercentage === "noPer"
            ? true
            : marksAssessmentScale.showAggregationAspercentage ===
                "aggregateAndPer"
              ? true
              : false,
        assessmentScaleMarkLevels: assessmentScaleMarkLevels,
        theoryMax: marksAssessmentScale.theoryRangeMax,
        theoryMin: marksAssessmentScale.theoryRangeMin,
        practicalMax: marksAssessmentScale.practicalRangeMax,
        practicalMin: marksAssessmentScale.practicalRangeMin,
      },
      assessmentModuleCode: arrLevel,
    };

    if (type === "create") {
      try {
        await createAssessmentScale(formData);
        setMarksAssessmentScale({
          scaleName: "",
          scoreName: "",
          rangeMin: 0,
          rangeMax: 100,
          theoryRangeMin: 0,
          theoryRangeMax: 100,
          practicalRangeMin: 0,
          practicalRangeMax: 0,
          minimumAchievement: 35,
          showScoreAsPercentage: "",
          calculateAggregate: false,
          showAggregationAspercentage: "",
        });
      } catch (error) {
        console.error("Create Failed", error);
      }
    } else {
      const updatedData = { ...formData, id: data.id };
      handleUpdateAssessment(updatedData);
    }
  };

  const handleAssociatePercentChange = (e, index) => {
    const { name, value } = e.target;
    const newAssociatePercent = associateScalePercent?.map((item, i) =>
      index === i ? { ...item, [name]: value } : item,
    );

    setAssociateScalePercent(newAssociatePercent);
  };

  const divideEqualParts = (min = 0, max = 100, parts) => {
    const step = (max - min) / parts;
    const ranges = [];

    for (let i = 0; i < parts; i++) {
      const minPercent = (min + i * step).toFixed(2);
      const maxPercent = (min + (i + 1) * step).toFixed(2);

      ranges.push({
        minPercent,
        maxPercent,
      });
    }

    return ranges;
  };

  useEffect(() => {
    if (assessmentScaleLevel?.assessmentScaleLevels?.length > 0) {
      const levels = assessmentScaleLevel?.assessmentScaleLevels;

      const ranges = divideEqualParts(
        marksAssessmentScale.rangeMin,
        marksAssessmentScale.rangeMax,
        levels.length,
      ).reverse();

      const data = levels.map((item, index) => ({
        id: item.id,
        grade: item?.level,
        descriptor: item?.descriptor,
        levelType: item?.levelType,
        rangeMin: ranges[index]?.minPercent,
        rangeMax: ranges[index]?.maxPercent,
      }));

      setAssociateScalePercent(data);
    }
  }, [assessmentScaleLevel, associateAssessmentLevel]);

  useEffect(() => {
    if (data && (type === "edit" || isView)) {
      const mark = data?.assessmentScaleMark;

      let showScoreAsPercentage = "";
      let showAggregationAspercentage = "";

      if (mark?.showScore && !mark?.showPercentage) {
        showScoreAsPercentage = "Nopctg";
      } else if (!mark?.showScore && mark?.showPercentage) {
        showScoreAsPercentage = "pctg";
      } else if (mark?.showScore && mark?.showPercentage) {
        showScoreAsPercentage = "both";
      }

      if (mark?.showAggregatedScore && !mark?.showAggregatedPercentage) {
        showAggregationAspercentage = "noPer";
      } else if (!mark?.showAggregatedScore && mark?.showAggregatedPercentage) {
        showAggregationAspercentage = "per";
      } else if (mark?.showAggregatedScore && mark?.showAggregatedPercentage) {
        showAggregationAspercentage = "aggregateAndPer";
      }

      setAssociateScalePercent(mark?.assessmentScaleMarkLevels);

      setMarksAssessmentScale({
        scaleName: data?.scaleName,
        scoreName: data?.scoreName,
        rangeMin: mark?.rangeMin,
        rangeMax: mark?.rangeMax,
        theoryRangeMin: mark?.theoryMin,
        theoryRangeMax: mark?.theoryMax,
        practicalRangeMin: mark?.practicalMin,
        practicalRangeMax: mark?.practicalMax,
        minimumAchievement: mark?.minAchievementLevel,
        showScoreAsPercentage,
        calculateAggregate: false,
        showAggregationAspercentage,
      });
    }
  }, [data, type, isView]);

  return (
    <>
      <div className={`arr-form grid ${type !== "create" ? "noShadow" : ""}`}>
        <div className="form-input">
          <label className="required" htmlFor="scaleName">
            Scale Name
          </label>
          <input
            className="form-control"
            type="text"
            value={marksAssessmentScale.scaleName}
            onChange={handleMarksAssessmentChange}
            name="scaleName"
            id="scaleName"
            placeholder="Enter scale name"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="scoreName">
            Score Name
          </label>
          <input
            className="form-control"
            value={marksAssessmentScale.scoreName}
            onChange={handleMarksAssessmentChange}
            type="text"
            name="scoreName"
            id="scoreName"
            placeholder="Enter score name"
            readOnly={isView}
            disabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="showScoreAsPercentage">
            Show Score As Percentage
          </label>
          <Select
            name={"showScoreAsPercentage"}
            value={
              showScoreAsPercentageOption?.find(
                (option) =>
                  option?.value === marksAssessmentScale.showScoreAsPercentage,
              ) || null
            }
            options={showScoreAsPercentageOption}
            onChange={(option) =>
              setMarksAssessmentScale((prev) => ({
                ...prev,
                showScoreAsPercentage: option?.value,
              }))
            }
            isDisabled={isView}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="range">
            Range
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              aria-label="rangeMin"
              type="number"
              name="rangeMin"
              className="form-control"
              disabled={true}
              value={marksAssessmentScale.rangeMin}
              placeholder="Min"
            />
            <input
              aria-label="rangeMax"
              type="number"
              min={0}
              max={100}
              name="rangeMax"
              className="form-control"
              onKeyDown={validateNegativeNumber}
              value={marksAssessmentScale.rangeMax}
              onChange={handleMarksAssessmentChange}
              placeholder="Max"
              readOnly={isView}
              disabled={isView}
            />
          </div>
        </div>

        <div className="form-input">
          <label className="required" htmlFor="minimumAchievement">
            Minimum Achievement
          </label>
          <input
            className="form-control"
            value={marksAssessmentScale.minimumAchievement}
            min={0}
            onKeyDown={validateNegativeNumber}
            onChange={handleMarksAssessmentChange}
            type="number"
            name="minimumAchievement"
            id="minimumAchievement"
            placeholder="Enter minimum achivement"
            readOnly={isView}
            disabled={isView || !marksAssessmentScale?.rangeMax}
          />
        </div>

        <div className="form-input">
          <label className="required" htmlFor="theoryRange">
            Theory Range
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="number"
              disabled={true}
              name="theoryRangeMin"
              aria-label="theoryRangeMin"
              className="form-control"
              value={marksAssessmentScale.theoryRangeMin}
              placeholder="Min"
            ></input>
            <input
              type="number"
              min={0}
              max={100}
              name="theoryRangeMax"
              aria-label="theoryRangeMax"
              className="form-control"
              value={marksAssessmentScale.theoryRangeMax}
              onKeyDown={validateNegativeNumber}
              onChange={handleMarksAssessmentChange}
              placeholder="Max"
              readOnly={isView}
              disabled={isView}
            />
          </div>
        </div>

        <div className="form-input">
          <label className="required" htmlFor="practicalRange">
            Practical Range
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="number"
              disabled={true}
              name="practicalRangeMin"
              aria-label="practicalRangeMin"
              className="form-control"
              value={marksAssessmentScale.practicalRangeMin}
              placeholder="Min"
            />
            <input
              type="number"
              min={0}
              max={100}
              name="practicalRangeMax"
              aria-label="practicalRangeMax"
              className="form-control"
              value={marksAssessmentScale.practicalRangeMax}
              onKeyDown={validateNegativeNumber}
              onChange={handleMarksAssessmentChange}
              placeholder="Max"
              readOnly={isView}
              disabled={isView}
            />
          </div>
        </div>

        <div className="form-input">
          <label className="required" htmlFor="showAggregationAspercentage">
            Show Aggregation As Percentage
          </label>
          <Select
            name={"showAggregationAspercentage"}
            options={showAggregationAsPercentageOption}
            value={
              showAggregationAsPercentageOption?.find(
                (o) =>
                  o.value === marksAssessmentScale.showAggregationAspercentage,
              ) || null
            }
            onChange={(option) =>
              setMarksAssessmentScale((prev) => ({
                ...prev,
                showAggregationAspercentage: option?.value,
              }))
            }
            isDisabled={isView}
          />
        </div>

        <div
          className="form-input"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <label htmlFor="calculateAggregate">
            Calculate Aggregate (of Subjects)
          </label>
          <Switch
            className="arr-switch"
            checked={marksAssessmentScale.calculateAggregate}
            onClick={(e) =>
              setMarksAssessmentScale((prev) => ({
                ...prev,
                calculateAggregate: !prev.calculateAggregate,
              }))
            }
            inputProps={{ "aria-label": "controlled" }}
            disabled={isView}
          />
        </div>
      </div>

      {!isView && (
        <div className="action-btns end" style={{ marginTop: "24px" }}>
          {type === "create" && (
            <Button
              variant="outline"
              onClick={() => {
                setAssociateLevelModal((prev) => !prev);
                const data = {
                  assessmentModuleCode: arrLevel,
                  assessmentScaleTypeCode: "sct-lvl",
                };
                getAssociateAssessmentLevel(data);
              }}
            >
              Associate Levels
            </Button>
          )}

          {type !== "create" && (
            <Button variant="outline" onClick={closeModal}>
              Cancel
            </Button>
          )}

          <Button disabled={loading.create || updating} onClick={handleSubmit}>
            {loading.create || updating ? (
              <Spinner size={"sm"} color="white" />
            ) : type === "create" ? (
              "Create Assessment Scale"
            ) : (
              "Update Assessment Scale"
            )}
          </Button>
        </div>
      )}

      <CustomModal
        isOpen={associateLevelModal}
        toggle={() => setAssociateLevelModal((prev) => !prev)}
        modalHeader={"Associate Levels"}
        size="md"
        children={
          <div>
            <div style={{ position: "relative" }}>
              <Select
                options={associateAssessmentLevelOptions}
                value={associateSelectInput}
                onChange={(option) => {
                  setAssociateSeletInput(option);
                  selectAssessmentScaleLevel(option?.value);
                }}
              />
            </div>
            <div className="arr-table-container">
              <table className="arr-table">
                <thead>
                  <tr>
                    <th>in (%)</th>
                    <th>Grade</th>
                    <th>Descriptor</th>
                  </tr>
                </thead>
                <tbody>
                  {associateScalePercent?.map((item, index) => {
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className="flex" style={{ gap: "10px" }}>
                            <input
                              type="number"
                              onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e") {
                                  e.preventDefault();
                                }
                              }}
                              min={0}
                              className="form-control"
                              name={"minPercent"}
                              value={associateScalePercent[index]?.rangeMin}
                              onChange={(e) =>
                                handleAssociatePercentChange(e, index)
                              }
                            />
                            <span>-</span>
                            <input
                              type="number"
                              onKeyDown={(e) => {
                                if (e.key === "-" || e.key === "e") {
                                  e.preventDefault();
                                }
                              }}
                              min={0}
                              className="form-control"
                              name={"maxPercent"}
                              value={associateScalePercent[index]?.rangeMax}
                              onChange={(e) =>
                                handleAssociatePercentChange(e, index)
                              }
                            />
                          </div>
                        </td>
                        <td>
                          {item?.levelType === "image" ? (
                            <img
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                              }}
                              src={
                                URL.imageSelectURL +
                                associateScalePercent[index]?.grade
                              }
                            />
                          ) : (
                            (associateScalePercent[index]?.grade ?? "N/A")
                          )}
                        </td>
                        <td>
                          {associateScalePercent[index]?.descriptor ?? "N/A"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="action-btns end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setAssociateLevelModal((prev) => !prev);
                  setAssociateScalePercent([]);
                  setAssociateSeletInput(null);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => setAssociateLevelModal((prev) => !prev)}
              >
                Done
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default MarksAssessmentForm;
